/**
 * The words on both sides of a representation request: the agent's form and
 * list (`REQUEST_COPY`), and the owner's inbox tab (`INBOX_COPY`).
 *
 * Two screens, one file, because they describe one record from either end;
 * the decisions about what is shown live in `representations.model.ts` for the
 * agent and `verifications.model.ts` for the owner. The copy departs from the
 * design in two places, argued on `REQUEST_COPY`, and both come from the same
 * fact: the screen must not act as a code oracle.
 */

import type { RepresentationOutcome } from "@ondo/shared";

/**
 * The Request Representation screen's copy.
 *
 * TWO DEPARTURES FROM THE DESIGN, both stated here rather than absorbed
 * quietly, because each is a thing the registry cannot honestly do.
 *
 * 1. THE BUTTON DOES NOT SAY "Verify". The design's form reads as a two-step
 *    check — verify the code, then ask — and there is no such step: the code is
 *    resolved and the request is written in one call, because a door that told
 *    you a code was real before you committed to asking would be a code oracle
 *    with an extra click in front of it. So the control says what pressing it
 *    does, and the sentence under it says what happens next.
 *
 * 2. THERE IS NO "EXAMPLE ONDO CODES" PANEL. The design lists four real works
 *    with their codes, which is a directory of other people's holdings on the
 *    one screen whose entire purpose is that you cannot get one. The panel's
 *    position is taken by the requests this account has actually made, which is
 *    what an agent standing here needs anyway: whether the last one was
 *    answered, and a way to take back one that was not.
 */
export const REQUEST_COPY = {
  /** The banner, which is the design's, minus the sentence about where to find a code. */
  lead:
    "Enter the ONDO code for a work you would like to represent. The code is on " +
    "the work’s record, and the collector is the only person who can give it to " +
    "you. Once sent, the owner reviews your request and grants or declines it.",

  /**
   * Shown instead of the form, to a seat below admin on a firm workspace.
   *
   * Names whose act it is rather than what the reader lacks. Approaching a
   * collector in the firm's name commits the firm to representing the work if
   * they say yes, which is why `ondo.request_representation()` asks for admin
   * on the principal being asked for.
   */
  needsAdmin:
    "Asking to represent a work approaches its owner in the firm’s name, so it is a director’s to send. Requests already made are listed below.",

  codeLabel: "ONDO code",
  codePlaceholder: "e.g. OND-7K4M-2QX9",

  confirmLabel: "Verify — artist’s name or year created",
  confirmPlaceholder: "e.g. Doig  ·  or  2003",
  /**
   * Says what the second field is FOR, because a form that asks for a fact you
   * have already typed the answer to reads as a bureaucratic double-entry.
   * The code is opaque — it carries no year and no initials — so this is a fact
   * somebody who has really been shown the work would know and a caller who
   * guessed the code would not.
   */
  confirmHelp:
    "Two facts, not one. The code identifies the work; the artist’s name or the " +
    "year it was made confirms you have actually been shown it. The code carries " +
    "neither, so this is a second fact rather than a second copy of the first.",

  submit: "Send request",
  sending: "Sending…",

  noteLabel: "Why you are asking (optional)",
  notePlaceholder: "The owner sees this. “I have a client for this work.”",

  /**
   * The refusal, in the agent's own words rather than the server's sentence.
   *
   * It names every possibility and settles on none, which is the whole design of
   * the door — see 20260831090000's decision 3. It also says the thing that
   * actually helps somebody who typed correctly and was refused: ask the
   * collector.
   */
  refused:
    "No work matches that code and confirmation, or it is not one you can ask to " +
    "represent. Check both with the collector — the code is on the work’s record.",
  throttled:
    "Too many checks this hour. This limit is on failed attempts, so it is asking " +
    "with the wrong details that spends it. Try again later.",
  error: "Couldn’t send that request.",

  sent: (title: string) => `Request sent for “${title}”. The owner decides from here.`,

  /** The list below the form. */
  madeTitle: "Requests you have made",
  madeEmpty: "You have not asked to represent anything yet.",
  madeLoading: "Loading your requests…",
  withdraw: "Withdraw",
  withdrawing: "Withdrawing…",
  awaiting: "Awaiting the owner",
} as const;

/** How a finished request reads in the list. */
export const OUTCOME_LABELS: Record<RepresentationOutcome, string> = {
  granted: "Granted",
  declined: "Declined",
  withdrawn: "Withdrawn",
};

/**
 * The owner's side, in the third tab of the verifications inbox.
 *
 * Its own object rather than fields on `VERIFICATION_COPY`, because this is not
 * a verification: nothing is being attested and nothing joins a chain of title.
 * It shares a screen with them because it shares a shape — somebody is waiting
 * on this reader for an answer — and that is the whole of the resemblance.
 */
export const INBOX_COPY = {
  tab: "Representation",
  empty: "No agent has asked to represent one of your works.",
  emptyDetail:
    "An agent asks by entering a work’s ONDO code, which is on the work’s record " +
    "and reaches them only if you give it to them.",
  asked: "What they are asking for",
  askedDetail: (name: string) =>
    `That ${name} may act for you on this work — read its record, correct the ` +
    `catalogue entry, and write its history. They cannot sell it, lend it, ` +
    `re-share it, or appoint anybody else.`,
  grant: "Grant",
  decline: "Decline",
  noteLabel: "A note back (optional)",
  notePlaceholder: "The agent sees this.",
  /** Never required, on either answer. See `answerRepresentationSchema`. */
  noteHelp: "Not required. “No thank you” is a complete answer.",
} as const;
